// src/features/owner/sections/users/UserRolesSection.tsx
import { useOwnerUsers } from '@/hooks';
import { useOwnerAction } from '@/hooks/useOwnerAction';
import { useSession } from '@/hooks/useSession';
import { Button, MetaRow } from '@/ui';
import { OwnerPanel, DataTable } from '../../components';
import type { Column } from '../../components';
import type { OwnerUserRow } from '@/core/models';

// The Council's three offices (spec 2026-08-13 §2). Held INDEPENDENTLY of a seat
// in the rotating moderator pool — the President is explicitly not a routine
// reviewer — so these drive their own actions and never touch
// setCouncilModerator. Superuser-only server-side, and the backend refuses a
// self-action, which is why the first President is seated by the
// seat_council_office management command rather than from this table.
const OFFICES = [
  { key: 'president', label: 'President', flag: 'isCouncilPresident' },
  { key: 'vice-president', label: 'Vice President', flag: 'isCouncilVicePresident' },
  { key: 'senator', label: 'Senator', flag: 'isCouncilSenator' },
] as const;

export function UserRolesSection() {
  const { data, loading, error, refetch } = useOwnerUsers();
  const action = useOwnerAction();
  const { isSuperuser } = useSession();

  const setOffice = {
    president: action.setCouncilPresident,
    'vice-president': action.setCouncilVicePresident,
    senator: action.setCouncilSenator,
  } as const;

  const columns: Column<OwnerUserRow>[] = [
    {
      key: 'handle',
      header: 'User',
      sortBy: (r) => r.handle,
      render: (r) => `${r.name} ${r.handle}`,
    },
    {
      key: 'role',
      header: 'Role',
      render: (r) => (r.isStaff ? 'Staff / Owner' : 'Member'),
    },
    {
      key: 'council',
      header: 'Council',
      render: (r) => (r.isCouncilModerator ? 'Seated' : 'None'),
    },
    {
      key: 'office',
      header: 'Office',
      render: (r) => {
        const held = OFFICES.filter((o) => r[o.flag]).map((o) => o.label);
        return held.length ? (
          <MetaRow>
            {held.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </MetaRow>
        ) : (
          'None'
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        isSuperuser ? (
          <Button
            variant="ghost"
            onClick={async () => {
              await action.setStaff(r.id, !r.isStaff);
              refetch();
            }}
          >
            {r.isStaff ? 'Revoke staff' : 'Grant staff'}
          </Button>
        ) : (
          <span style={{ opacity: 0.5 }}>{r.isStaff ? 'Staff' : 'Member'}</span>
        ),
    },
    {
      // Seating the Council is the only route into the pool assemble_team()
      // draws from, and it is superuser-only server-side. Kept as its own
      // column rather than folded into the staff button: they are unrelated
      // powers, and conflating them is how someone ends up on the body that
      // judges every member because they were made staff.
      key: 'council-actions',
      header: '',
      render: (r) =>
        isSuperuser ? (
          <Button
            variant="ghost"
            onClick={async () => {
              await action.setCouncilModerator(r.id, !r.isCouncilModerator);
              refetch();
            }}
          >
            {r.isCouncilModerator ? 'Remove from Council' : 'Seat on Council'}
          </Button>
        ) : null,
    },
    // One control per office, mirroring the Council-seat control above. Kept
    // separate from each other and from the pool seat for the same reason:
    // they are unrelated powers, and one button that grants several is how
    // somebody ends up with authority nobody meant to give them.
    ...OFFICES.map<Column<OwnerUserRow>>((o) => ({
      key: `office-${o.key}`,
      header: '',
      render: (r) =>
        isSuperuser ? (
          <Button
            variant="ghost"
            onClick={async () => {
              await setOffice[o.key](r.id, !r[o.flag]);
              refetch();
            }}
          >
            {r[o.flag] ? `Remove as ${o.label}` : `Seat as ${o.label}`}
          </Button>
        ) : null,
    })),
  ];
  if (loading) return <p style={{ opacity: 0.6 }}>Loading…</p>;
  return (
    <OwnerPanel title="Roles">
      {/*
        Two of these three offices are no longer inert, so the notice no longer
        says all three are — and it still says the Senator seat is, because that
        control IS wired to nothing and overstating reach is the same defect as
        understating it. It also names the superuser requirement: seating a
        President does not confer the ability to appoint, and a non-superuser
        President gets a bare DRF 403 that officeFailure cannot even translate.
        Factual, not apologetic.

        The last sentence used to read "Appointment is made over the API; there
        is no team surface in this console yet." Council Teams is that surface,
        so the sentence became false and was replaced rather than quietly left —
        a notice nobody re-reads is how a console starts lying about itself.
      */}
      <p style={{ opacity: 0.7, margin: '0 0 12px' }}>
        The President and Vice President appoint a head moderator to each review team, and a
        case waits for that appointment before its team can vote, but only if the account is
        also a superuser; the office alone is not enough. The Senator seat is recorded and does
        not yet affect how cases are handled. Teams are still drawn at random from the Council
        pool, and a case still resolves on the team&rsquo;s vote. Judgement by the head, and
        escalation to the Senate, arrive in a later release. Appointments are made in the
        Council Teams section, under Content Management.
      </p>
      <DataTable<OwnerUserRow>
        columns={columns}
        rows={data ?? []}
        rowKey={(r) => r.id}
        emptyIcon="user"
        emptyMessage={error ?? 'No users yet'}
      />
    </OwnerPanel>
  );
}
